/**
 * nalli-header.js — Nalli rebuild WS3 (header & navigation)
 * Loaded from snippets/nalli-head.liquid as a deferred module.
 *
 * Only what CSS cannot do:
 * 1. Tabbed mega-menu hover panel switching (from live snippets/mega-menu.liquid
 *    inline script — live duplicated it once per menu item; single module here).
 * 2. Wishlist card-title height equalizer on /apps/wishlist (from live
 *    assets/header.js — 1s setInterval replaced with ResizeObserver +
 *    MutationObserver; the app injects its DOM client-side).
 *
 * Dropped vs live (now pure CSS in nalli-header.css):
 * - announcement-bar sticky class script (sections/header-announcements.liquid)
 * - announcement z-index toggle on drawer open/close (sections/header.liquid
 *   inline script + assets/header-drawer.js .stick-announce patch)
 */

/* ------------------------------------------------------------------ *
 * 1. Tabbed mega menu — hover switches panels                         *
 * ------------------------------------------------------------------ */
const initializedGrids = new WeakSet();

function initNalliMegaMenus(root = document) {
  root.querySelectorAll('.mega-menu__grid').forEach((grid) => {
    if (initializedGrids.has(grid)) return;
    const tabs = grid.querySelectorAll('.mega-menu__tab');
    const panels = grid.querySelectorAll('.mega-menu__panel');
    if (!tabs.length) return;
    initializedGrids.add(grid);

    function activate(tab) {
      const target = tab.getAttribute('data-tab');
      tabs.forEach((t) => t.classList.remove('active'));
      panels.forEach((p) => p.classList.remove('active'));
      tab.classList.add('active');
      const activePanel = grid.querySelector(`.mega-menu__panel[data-panel="${target}"]`);
      if (activePanel) activePanel.classList.add('active');
    }

    // First tab/panel active by default
    activate(tabs[0]);

    // Switch when hovering tabs
    tabs.forEach((tab) => {
      tab.addEventListener('mouseenter', () => activate(tab));
    });

    // Keep active when hovering inside a panel (live safeguard)
    panels.forEach((panel) => {
      panel.addEventListener('mouseenter', () => {
        const target = panel.getAttribute('data-panel');
        const matchingTab = grid.querySelector(`.mega-menu__tab[data-tab="${target}"]`);
        if (matchingTab) activate(matchingTab);
      });
    });
  });
}

/* ------------------------------------------------------------------ *
 * 2. Wishlist h3 equalizer — /apps/wishlist app-proxy page only       *
 *    (".wihslist-meta" [sic] matches the wishlist app's own markup)   *
 * ------------------------------------------------------------------ */
function equalizeWishlistTitles() {
  let max = -1;
  const titles = document.querySelectorAll('.wishlist-column .wihslist-meta h3');
  if (!titles.length) return;
  titles.forEach((h3) => {
    h3.style.height = 'auto';
    max = Math.max(max, h3.offsetHeight);
  });
  if (max > 0) {
    document.querySelectorAll('.wihslist-meta h3').forEach((h3) => {
      h3.style.height = `${max}px`;
    });
  }
}

function initWishlistEqualizer() {
  if (!window.location.href.includes('/apps/wishlist')) return;

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      equalizeWishlistTitles();
    });
  };

  // The wishlist app injects/updates its grid client-side: watch for it.
  const mutationObserver = new MutationObserver(schedule);
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  // Re-equalize when card widths change (viewport resize / grid reflow).
  const resizeObserver = new ResizeObserver(schedule);
  const attachResizeTargets = () => {
    document.querySelectorAll('.wishlist-column').forEach((col) => resizeObserver.observe(col));
  };
  attachResizeTargets();
  // Late-injected columns get picked up via the mutation observer.
  const lateAttach = new MutationObserver(attachResizeTargets);
  lateAttach.observe(document.body, { childList: true, subtree: true });

  schedule();
}

/* ------------------------------------------------------------------ *
 * 3. Syte camera relocator — the Syte app embed injects its header    *
 *    camera button against the published theme's DOM                  *
 *    (data-element-selector "cart-drawer-component                    *
 *    .header-actions__action"); Horizon 4.1.3 has no                  *
 *    cart-drawer-component around the header cart button, so the      *
 *    button falls back to an offscreen float. Move it in front of the *
 *    cart action, matching its slot on the published theme            *
 *    (wishlist · camera · cart).                                      *
 * ------------------------------------------------------------------ */
function relocateSyteCamera() {
  const place = () => {
    const cam = document.querySelector(
      'button.--syte-start-camera-upload[data-camera-button-placement="header"]'
    );
    if (!cam) return false;
    const headerActions = document.querySelector('header-actions');
    if (!headerActions) return false;
    if (headerActions.contains(cam)) return true;
    const cartAction = headerActions.querySelector('button.header-actions__action');
    if (!cartAction) return false;
    cartAction.parentElement.insertBefore(cam, cartAction);
    return true;
  };

  if (place()) return;
  // The app injects late; watch until its button shows up.
  const observer = new MutationObserver(() => {
    if (place()) observer.disconnect();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  // Stop watching after 20s — app not installed / embed disabled.
  setTimeout(() => observer.disconnect(), 20000);
}

/* ------------------------------------------------------------------ */
function init() {
  initNalliMegaMenus();
  initWishlistEqualizer();
  relocateSyteCamera();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Theme editor: re-bind after section re-render
document.addEventListener('shopify:section:load', (event) => {
  initNalliMegaMenus(event.target || document);
});
